import { capitalize, intersection, snakeCase } from "lodash-es";
import { findingSeverities, slaStatuses } from "../../FindingsUtils";
import { cloudProviderIcons } from "../../../icons/CloudProviderIcons/CloudProviderIconsMap";
import {
  slaTypes,
  TicketSlaStatus,
} from "../../../CustomizedDashboard/Widgets/SLA/SLAUtils";
import FindingStatusLogo from "../../../AppSettings/RiskCustomization/SubStatuses/SubStatusesByCategoryItem/FindingStatusLogo/FindingStatusLogo";
import SLAStatus from "../../SLAStatus/SLAStatus";
import { SeverityFieldsEnum } from "../../../General/useSeverityAsPriority";
import PriorityTag from "../../../Components/PriorityTag/PriorityTag";
import {
  convertToBeautifulString,
  TicketStatus,
  ticketStatusEnum,
} from "@SeemplicityDev/shared";
import { getAdditionalDataTitle } from "../../FindingDetailsPane/FindingsDetailsPaneComponents/Details/DetailsUtils";
import { readOnlyFields } from "../FilterUtils";
import {
  getEdrLevelMeta,
  resolveEdrDisplayLabel,
  type EdrLevelMeta,
} from "../../FindingDetailsPane/FindingsDetailsPaneComponents/Remediation/EDRTooltip/edrLevels";
import type { TreeNode } from "../../../Components/Tree/types";
import {
  isActualStatusKey,
  splitFilterKey,
} from "../../../AppView/FilterManagement/filterKey";
import { filterFieldsEnum } from "../../../AppView/FilterManagement/filterFieldsEnum";
import { getAnalystPriorityDirectionDisplay } from "../../analystPriorityDirection";

// ============================================================================
// Enums
// ============================================================================

export const conditionGroupsEnum = Object.freeze({
  MEMBERSHIP: "MEMBERSHIP",
  OVERLAP: "OVERLAP",
  LIKELIST: "LIKELIST",
  LIKE: "LIKE",
  COMPARE: "COMPARE",
  BOOLEAN: "BOOLEAN",
  RECENT: "RECENT",
  WITHIN: "WITHIN",
  DEPRECATED: "DEPRECATED",
});

export const conditionsEnum = Object.freeze({
  LT: "lt",
  LTE: "lte",
  GT: "gt",
  GTE: "gte",
  RANGE: "range",
  EQ: "eq",
  LIKE: "like",
  NOTLIKE: "notlike",
  IN: "in",
  NOT_IN: "not_in",
  ILIKE: "ilike",
  OVERLAP: "overlap",
  NOTOVERLAPPING: "notoverlapping",
  LIKELIST: "likelist",
  NOTLIKELIST: "notlikelist",
  OVERLAPLIKE: "overlaplike",
  OVERLAPILIKE: "overlapilike",
  NOTOVERLAPILIKE: "notoverlapilike",
  NOTOVERLAPILIKELIST: "notoverlapilikelist",
  OVERLAPILIKELIST: "overlapilikelist",
  OVERLAPEXACTMATCH: "overlapexactmatch",
  NOTOVERLAPEXACTMATCH: "notoverlapexactmatch",
  EXISTS: "exists",
  RECENT: "recent",
  WITHIN: "within",
});

export const fieldTypesEnum = Object.freeze({
  STRING: "string",
  NUMBER: "number",
  INT: "int",
  FLOAT: "float",
  DATETIME: "datetime",
});

export { filterFieldsEnum };

export const valueUnitsEnum = Object.freeze({
  DAYS: "days",
  ABSOLUTE: "absolute",
});

export const treeNodeTypesEnum = Object.freeze({
  CATEGORY: "category",
  KEY: "key",
});

// ============================================================================
// Condition labels, groups & metadata
// ============================================================================

interface ConditionLabelEntry {
  short: string;
  long: string;
}

export const conditionLabels: Record<string, ConditionLabelEntry> = {
  lt: { short: "<", long: "Less than (<)" },
  lte: { short: "<=", long: "Less than or Equal to (<=)" },
  gt: { short: ">", long: "Greater Than (>)" },
  gte: { short: ">=", long: "Greater Than or Equal to (>=)" },
  range: { short: "Range", long: "Range" },
  eq: { short: "=", long: "Equals (=)" },
  like: { short: "*", long: "Like (*)" },
  notlike: { short: "!*", long: "Not Like (!*)" },
  in: { short: "=", long: "Equals (=)" },
  ilike: { short: "I-Like", long: "I-Like" },
  not_in: { short: "!=", long: "Not equals (!=)" },
  overlap: { short: "=", long: "Equals (=)" },
  notoverlapping: { short: "!=", long: "Not equals (!=)" },
  likelist: { short: "*", long: "Like (*)" },
  notlikelist: { short: "!*", long: "Not Like (!*)" },
  overlaplike: { short: "Like*", long: "Like*" }, // deprecated
  overlapilike: { short: "I-Like*", long: "I-Like*" }, // deprecated
  notoverlapilike: { short: "Not I-Like*", long: "Not I-Like*" }, // deprecated
  notoverlapilikelist: { short: "!*", long: "Not Like (!*)" },
  overlapilikelist: { short: "*", long: "Like (*)" },
  overlapexactmatch: { short: "=", long: "Equals (=)" },
  notoverlapexactmatch: { short: "!=", long: "Not equals (!=)" },
  exists: { short: "Exists", long: "Exists" },
  recent: { short: "=", long: "Equals (=)" },
};

export const conditionDateLabel: Record<string, string> = {
  lt: "Before",
  gt: "After",
  within: "Within",
  recent: "=",
};

export const conditionTypes: Record<string, string> = {
  lt: "NEQ",
  lte: "NEQ",
  gt: "NEQ",
  gte: "NEQ",
  //range: "Range",
  eq: "EQ",
  like: "LIKE",
  notlike: "LIKE",
  in: "EQ",
  not_in: "EQ",
  overlap: "OVERLAP",
  notoverlapping: "OVERLAP",
  likelist: "LIKELIST",
  notlikelist: "LIKELIST",
  notoverlapilikelist: "LIKELIST",
  overlapilikelist: "LIKELIST",
  overlapexactmatch: "LIKELIST",
  notoverlapexactmatch: "LIKELIST",
};

// conditionGroups depends on conditionGroupsEnum (declared above).
export const conditionGroups: Record<string, string> = {
  in: conditionGroupsEnum.MEMBERSHIP,
  not_in: conditionGroupsEnum.MEMBERSHIP,
  overlap: conditionGroupsEnum.OVERLAP,
  notoverlapping: conditionGroupsEnum.OVERLAP,
  likelist: conditionGroupsEnum.LIKELIST,
  notlikelist: conditionGroupsEnum.LIKELIST,
  notoverlapilikelist: conditionGroupsEnum.LIKELIST,
  overlapilikelist: conditionGroupsEnum.LIKELIST,
  overlapexactmatch: conditionGroupsEnum.LIKELIST,
  notoverlapexactmatch: conditionGroupsEnum.LIKELIST,
  like: conditionGroupsEnum.LIKE,
  notlike: conditionGroupsEnum.LIKE,
  ilike: conditionGroupsEnum.LIKE,
  lt: conditionGroupsEnum.COMPARE,
  lte: conditionGroupsEnum.COMPARE,
  gt: conditionGroupsEnum.COMPARE,
  gte: conditionGroupsEnum.COMPARE,
  exists: conditionGroupsEnum.BOOLEAN,
  recent: conditionGroupsEnum.RECENT,
  within: conditionGroupsEnum.WITHIN,
  overlaplike: conditionGroupsEnum.DEPRECATED,
  overlapilike: conditionGroupsEnum.DEPRECATED,
  notoverlapilike: conditionGroupsEnum.DEPRECATED,
};

export const getConditionGroup = (condition: string): string | undefined =>
  conditionGroups[condition];

export const getIsDateField = (
  fieldType?: string,
  conditions?: string[]
): boolean =>
  Boolean(
    fieldType === fieldTypesEnum.DATETIME ||
      conditions?.includes(conditionsEnum.RECENT) ||
      conditions?.includes(conditionsEnum.WITHIN)
  );

export const isSetGroup = (group?: string): boolean =>
  group === conditionGroupsEnum.MEMBERSHIP ||
  group === conditionGroupsEnum.OVERLAP;

// ============================================================================
// Condition classification lists
// ============================================================================

export const deprecatedConditions: string[] = [
  "overlaplike",
  "overlapilike",
  "notoverlapilike",
];

// ============================================================================
// Field-name lists (by domain / value type)
// ============================================================================

export const generalFields: string[] = [
  "age",
  "category",
  "discovered_time",
  "due_date",
  "finding_type_str",
  "finding_type",
  "is_aggregated",
  "last_reported_time",
  "last_collected_time",
  "resolved_time",
  "original_score",
  "original_severity",
  "original_status",
  "score",
  "severity",
  "sla_remaining_time",
  "sla_status",
  "sub_category",
  "title",
  "source",
];
export const ticketFields: string[] = [
  "has_ticket",
  "ticket_due_date_status",
  "ticket_status",
  "ticket_external_id",
  "ticket_assignee",
  "ticket_due_date",
  "ticket_created_time",
  "ticket_age",
];
export const packageFields: string[] = [
  "package",
  "package_name",
  "vulnerable_versions",
  "fixed_versions",
  "package_version",
  "cpe",
];
export const vulnerabilityFields: string[] = [
  "cve",
  "cvss_score",
  "cvss_vector",
  "cvss_v4_score",
  "cvss_v4_vector",
  "cvss_v3_score",
  "cvss_v3_temporal_score",
  "cvss_v3_vector",
  "cvss_v2_score",
  "cvss_v2_temporal_score",
  "cvss_v2_vector",
  "epss_score",
  "epss_percentile",
  "cwe",
  "cisa_kev",
  "cisa_kev_due_date",
  "cwe_lists",
  "vulncheck_kev",
  "rce",
  "unauthenticated_rce",
  "exploit_maturity",
  "crowdstrike_exploit_status",
  "vulnerability_reported_date",
];

export const remediationFields: string[] = [
  "fix_type",
  "fix_subtype",
  "fix_id",
  "fix_vendor_id",
  "fix_extra_identifiers",
  "mitigation_level",
];

export const rulesFields: string[] = ["rule_family", "rule_id", "rule_type"];

export const aiAnalysisFields: string[] = [
  "last_analyzed_ts",
  "ai_analyst_priority",
  "ai_analyst_priority_direction",
  "has_ai_analysis",
  "analyst_instance_id",
  "supported_analyst",
  "is_analyzable",
];

export const ageFields: string[] = ["age", "sla_remaining_time", "ticket_age"];

export const fieldsToConvertToBeautifulString: string[] = ["resource_type"];

// ============================================================================
// Filter categories & ordering
// ============================================================================

export const categoryNames: Record<string, string> = {
  GENERAL: "Findings Attributes",
  TICKET: "Ticket",
  PACKAGE: "Package",
  VULNERABILITY: "Vulnerability",
  REMEDIATION: "Remediation",
  RULES: "Rules",
  AI_ANALYSIS: "AI Analysis",
  ADDITIONAL_DATA: "Additional Data",
};

interface FilterSubCategory {
  field: string;
  name: string;
  isMenu: boolean;
  itemsByOrder: string[] | null;
}

// filtersSubCategories depends on categoryNames and the field-name lists above.
export const filtersSubCategories: FilterSubCategory[] = [
  {
    field: "general_category",
    name: categoryNames.GENERAL,
    isMenu: false,
    itemsByOrder: generalFields,
  },
  {
    field: "tickets_category",
    name: categoryNames.TICKET,
    isMenu: true,
    itemsByOrder: ticketFields,
  },
  {
    field: "package_category",
    name: categoryNames.PACKAGE,
    isMenu: true,
    itemsByOrder: packageFields,
  },
  {
    field: "vulnerability_category",
    name: categoryNames.VULNERABILITY,
    isMenu: true,
    itemsByOrder: vulnerabilityFields,
  },
  {
    field: "remediation_category",
    name: categoryNames.REMEDIATION,
    isMenu: true,
    itemsByOrder: remediationFields,
  },
  {
    field: "rules_category",
    name: categoryNames.RULES,
    isMenu: true,
    itemsByOrder: rulesFields,
  },
  {
    field: "ai_analysis_category",
    name: categoryNames.AI_ANALYSIS,
    isMenu: true,
    itemsByOrder: aiAnalysisFields,
  },
  {
    field: "additional_data_category",
    name: categoryNames.ADDITIONAL_DATA,
    isMenu: true,
    itemsByOrder: null,
  },
];

// Display order for known categories; unknown ones are appended alphabetically.
export const scopeCategoriesSort: string[] = [
  "Resource",
  "Cloud Infrastructure",
  "Machines",
  "Code",
  "Tags",
];

// Must match the engine's DEFAULT_FILTER_CATEGORY.
const fallbackScopeCategory = "Other";

export const categoriesFiltersSort: string[] = [
  "general_category",
  "vulnerability_category",
  "package_category",
  "ai_analysis_category",
  "remediation_category",
  "tickets_category",
  "rules_category",
  "additional_data_category",
];

// ============================================================================
// Option builders (value pickers)
// ============================================================================

export const getPlaceholderByField = (field?: string): string => {
  switch (splitFilterKey(field).property) {
    case "age":
    case "discovered_time":
    case "ticket_age":
    case "ticket_created_time":
    case "ticket_due_date":
    case "last_reported_time":
    case "resolved_time":
    case "sla_remaining_time":
    case "cisa_kev_due_date":
    case "knowbe4_training_campaign_end_date":
    case "vulnerability_reported_date":
    case "last_collected_time":
      return "Custom #";

    case "last_seen":
    case "first_seen":
      return "Custom #";

    case "due_date":
      return "Number of days";

    case "epss_percentile":
      return "Enter Percentile (e.g.: 99.055)";

    default:
      return "";
  }
};

interface SelectOption {
  label?: string;
  value: string;
  name: string;
  customOption?: {
    type: string;
    component: string;
    placeholder: string;
    onError?: () => void;
    min: number;
  };
  // These options are spread into RadioButtonsList menu options downstream;
  // the index signature keeps them assignable to the consumer's MenuOption.
  [key: string]: unknown;
}

// dateOptions / ageOptions depend on getPlaceholderByField (declared above).
export const dateOptions = (
  field: string,
  onCustomOptionError?: () => void
): SelectOption[] => {
  return [
    { label: "Today", value: "1", name: "1" },
    { label: "Last 7 Days", value: "7", name: "7" },
    { label: "Last 30 Days", value: "30", name: "30" },
    { label: "Last 90 Days", value: "90", name: "90" },
    {
      customOption: {
        type: "number",
        component: "TEXT",
        placeholder: getPlaceholderByField(field),
        onError: onCustomOptionError,
        min: 0,
      },
      value: "custom",
      name: "custom",
    },
  ];
};

export const booleanOptions: SelectOption[] = [
  {
    label: "True",
    value: "True",
    name: "true",
  },
  {
    label: "False",
    value: "False",
    name: "false",
  },
];

export const ageOptions = (
  field: string,
  onCustomOptionError?: () => void
): SelectOption[] => {
  return [
    { label: "7 Days", value: "7", name: "7" },
    { label: "30 Days", value: "30", name: "30" },
    { label: "90 Days", value: "90", name: "90" },
    {
      customOption: {
        type: "number",
        component: "TEXT",
        placeholder: getPlaceholderByField(field),
        onError: onCustomOptionError,
        min: 0,
      },
      value: "custom",
      name: "custom",
    },
  ];
};

// ============================================================================
// Option list sorting & assembly
// ============================================================================

// Item option produced/consumed by the alphabetical sort helper. `label` drives
// the comparison; other props are passed through.
interface ItemOption {
  label: string;
  [key: string]: unknown;
}

export const sortItemOptions = <T extends ItemOption>(
  options?: T[]
): T[] | undefined => {
  return options?.sort((a, b) => {
    const labelA = a.label.toUpperCase(); // Convert to uppercase for case-insensitive sorting
    const labelB = b.label.toUpperCase();

    if (labelA < labelB) {
      return -1;
    }
    if (labelA > labelB) {
      return 1;
    }
    return 0;
  });
};

// A field/scope descriptor coming from the GraphQL filter metadata. `field` is
// the only required member; everything else is spread through onto the produced
// option items.
interface FilterFieldDescriptor {
  field: string;
  [key: string]: unknown;
}

// A grouped category produced by getListOptions: a menu item carrying its
// selectable filter/scope fields under `subItems`.
interface ListOptionCategory {
  field: string;
  label: string;
  value: string;
  isMenu: boolean;
  subItems: Array<Record<string, unknown> & { label: unknown; value: string }>;
}

export const getListOptions = (
  data: FilterFieldDescriptor[] | undefined,
  isForFilter: boolean
): ListOptionCategory[] | undefined => {
  // Display label is resolved upstream in useFiltersData (severity/priority terminology aware); fall
  // back to the static title for anything without one.
  const labelFor = (filter: FilterFieldDescriptor): unknown =>
    filter.label ?? getLabelByField(filter, "field");
  if (isForFilter) {
    const filtersList = data?.reduce((acc, filter) => {
      let inAdditionalData = true; // If the filter field is not assigned to any category- it'll be added to additional data category
      const { property } = splitFilterKey(filter.field);
      filtersSubCategories.forEach((subCategory) => {
        if (subCategory.itemsByOrder?.includes(property)) {
          const subCategoryData: ListOptionCategory = acc.get(
            subCategory.field
          ) || {
            field: subCategory.field,
            label: getLabelByField(subCategory, "name"),
            value: subCategory.field,
            isMenu: true,
            subItems: [],
          };
          subCategoryData.subItems.push({
            ...filter,
            label: labelFor(filter),
            value: filter.field,
          });
          acc.set(subCategory.field, subCategoryData);
          inAdditionalData = false;
        }
      });

      if (inAdditionalData) {
        const additionalDataCategory = filtersSubCategories.find(
          (category) => category.name === categoryNames.ADDITIONAL_DATA
        );
        const additionalDataItem: ListOptionCategory = acc.get(
          additionalDataCategory!.field
        ) || {
          field: additionalDataCategory!.field,
          label: getLabelByField(additionalDataCategory!, "name"),
          value: additionalDataCategory!.field,
          isMenu: true,
          subItems: [],
        };
        additionalDataItem.subItems.push({
          ...filter,
          label: labelFor(filter),
          value: filter.field,
        });
        acc.set(additionalDataItem.field, additionalDataItem);
      }

      return acc;
    }, new Map<string, ListOptionCategory>());

    const fieldsArray = Array.from(filtersList?.values() ?? []);
    fieldsArray.forEach((field) => {
      field.subItems = sortItemOptions(
        field.subItems as Array<ItemOption & { value: string }>
      ) as ListOptionCategory["subItems"];
    });

    return fieldsArray;
  } else {
    const scopesList = data?.reduce((acc, scope) => {
      // The BE excludes these, but not every useFiltersData consumer does.
      if (readOnlyFields.includes(scope.field)) {
        return acc;
      }

      const category =
        (scope.category as string | undefined) || fallbackScopeCategory;

      const categoryData: ListOptionCategory = acc.get(category) || {
        field: snakeCase(category),
        label: category,
        value: category,
        isMenu: true,
        subItems: [],
      };

      categoryData.subItems.push({
        ...scope,
        label: labelFor(scope),
        value: scope.field,
      });

      acc.set(category, categoryData);

      return acc;
    }, new Map<string, ListOptionCategory>());

    return sortScopeCategories(Array.from(scopesList?.values() ?? []));
  }
};

const sortScopeCategories = (
  categories: readonly ListOptionCategory[]
): ListOptionCategory[] =>
  [...categories].sort((a, b) => {
    const aIndex = scopeCategoriesSort.indexOf(a.label);
    const bIndex = scopeCategoriesSort.indexOf(b.label);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) {
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }

    return a.label.localeCompare(b.label);
  });

// The "field" argument is because we couldn't name the field of the subcategories as
// its display name - since some filters have the same name, so we needed to separate
// the properties and to get the label for a field name - we needed to make this function generic to which field to refer.
const getLabelByField = (obj: object, field: string): string => {
  return getAdditionalDataTitle(
    (obj as Record<string, unknown>)[field] as string
  );
};

// ============================================================================
// Value label & option enrichment
// ============================================================================

// supported_analyst values are the backend's analyst type names; SAST, SCA and None read as-is.
const supportedAnalystLabels: Record<string, string> = {
  HOST_VM: "Host VM",
  WEB: "Web",
  CONTAINER: "Container",
};

const getFieldValueLabel = (fieldValue?: string, value?: unknown): unknown => {
  const property = splitFilterKey(fieldValue).property;
  if (
    property === filterFieldsEnum.SEVERITY ||
    property === filterFieldsEnum.AI_ANALYST_PRIORITY
  ) {
    return (findingSeverities as Record<string, string>)[value as string];
  } else if (property === filterFieldsEnum.AI_ANALYST_PRIORITY_DIRECTION) {
    return (
      getAnalystPriorityDirectionDisplay(value as string)?.label ??
      capitalize(value as string)
    );
  } else if (property === "ticket_status") {
    return (ticketStatusEnum as Record<string, { text?: string } | undefined>)[
      value as string
    ]?.text;
  } else if (property === "sla_status") {
    return (slaStatuses as Record<string, { title?: string } | undefined>)[
      value as string
    ]?.title;
  } else if (property === "ticket_due_date_status") {
    return (TicketSlaStatus as Record<string, string>)[value as string];
    // there was a requirement to show null value as "null": https://betterviewcloud.atlassian.net/browse/WAK-9626
  } else if (
    (property === "cvss_v2_temporal_score" ||
      property === "cvss_v2_score" ||
      property === "cvss_v3_score" ||
      property === "cvss_v4_score") &&
    value === null
  ) {
    return "null";
  } else if (property === "resource_type") {
    return convertToBeautifulString(value as string);
  } else if (property === "supported_analyst") {
    return supportedAnalystLabels[value as string] ?? value;
  }

  return value;
};

export const filterFieldValuesBySearch = (
  fieldValue: string | undefined,
  values: unknown[] | undefined,
  search: string
): unknown[] => {
  const list = values ?? [];
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return list;
  }
  return list.filter((value) => {
    const raw = String(value ?? "");
    const label = getFieldValueLabel(fieldValue, value);
    const labelStr = String(label ?? "");
    return (
      raw.toLowerCase().includes(needle) ||
      labelStr.toLowerCase().includes(needle)
    );
  });
};

export const getFieldValueOptions = (
  fieldValue: string | undefined,
  array: unknown[] | undefined,
  currentTheme = "LIGHT"
): Array<Record<string, unknown>> | undefined => {
  const property = splitFilterKey(fieldValue).property;
  return array?.map((rawValueOption) => {
    let valueOption = rawValueOption;
    let optionImg: string | null = null;
    let optionIcon: unknown = null;
    if (property === "cloud_provider") {
      // cloudProviderIcons entries are keyed by "LIGHT" | "DARK"; currentTheme is
      // a free string at the call sites, so read through a string-indexed view.
      optionImg =
        ((
          cloudProviderIcons[
            snakeCase((valueOption as string)?.toLowerCase())
          ] as Record<string, string | null | undefined> | undefined
        )?.[currentTheme] as string | null | undefined) ?? null;
    } else if (property === "ticket_status") {
      // JS map with literal-inferred keys; index via Record cast.
      optionIcon = (
        ticketStatusEnum as Record<string, { icon?: unknown } | undefined>
      )[valueOption as string]?.icon;
    } else if (property === "tenable_severity_score") {
      // This is necessary because tenable_severity_score values are saved
      // As a local string on findings_view
      valueOption = capitalize(valueOption as string);
    }
    return {
      label: getFieldValueLabel(fieldValue, valueOption),
      value: [valueOption],
      leftImg: optionImg,
      leftIcon: optionIcon,
    };
  });
};

// Option passed into / returned from enrichFilterOptions. `value` is the
// runtime value tuple (e.g. `[severityValue]`); other props are passed through.
interface EnrichableOption {
  label?: unknown;
  value?: Array<string | number | undefined>;
  [key: string]: unknown;
}

// getOptionsCallback returns a per-field bag of helpers; the consumers narrow
// to whichever members the matched field needs.
type EnrichOptionsCallback = (field: string) => {
  getStatuses?: () => unknown;
  getSeverityField?: (
    value: string | number | undefined,
    field: string
  ) => string;
  severityAsPriorityActive?: boolean;
  getDataSourceIcon?: (dataSource: string, size?: "S" | "L" | null) => unknown;
  getAnalystDisplayName?: (id: unknown) => string;
} | null;

export const enrichFilterOptions = (
  field: string | undefined,
  // Options come from the untyped getFieldValueOptions util and are widened at
  // the call sites (e.g. ValueOption[]); accept the permissive record shape and
  // read the runtime value tuple via the EnrichableOption view below.
  rawOptions: Array<Record<string, unknown>> | undefined,
  getOptionsCallback: EnrichOptionsCallback
): Array<Record<string, unknown>> | undefined => {
  const options = rawOptions as EnrichableOption[] | undefined;
  if (isActualStatusKey(field)) {
    const { getStatuses } = getOptionsCallback(field ?? "") ?? {};
    return getStatuses?.() as Array<Record<string, unknown>> | undefined;
  }

  switch (splitFilterKey(field).property) {
    case "ticket_status":
      return options?.map((option) => ({
        ...option,
        label: option?.label,
        customPrefix: (
          <div className={"ticket-status-filter-option"}>
            <TicketStatus
              status={option?.value?.[0] as string}
              withLabel={false}
            />
          </div>
        ),
      }));
    case "sla_status":
      return options?.map((option) => ({
        ...option,
        label: <SLAStatus sla_status={option?.value?.[0] as string} />,
      }));
    case "ticket_due_date_status":
      return options?.map((option) => ({
        ...option,
        label: (
          <SLAStatus
            sla_status={option?.value?.[0] as string}
            slaType={slaTypes.TICKET}
          />
        ),
      }));
    case "ai_analyst_priority":
    case "severity":
      const { getSeverityField, severityAsPriorityActive } =
        getOptionsCallback(field ?? "") ?? {};
      return (
        options
          // main subtracts the raw `value` tuples, relying on single-element
          // array → number coercion; preserve that exact expression.
          ?.sort(
            (a, b) =>
              (b.value as unknown as number) - (a.value as unknown as number)
          )
          ?.map((option) => ({
            ...option,
            label: severityAsPriorityActive ? (
              <PriorityTag severity={option?.value?.[0]} />
            ) : (
              capitalize(
                getSeverityField?.(option?.value?.[0], SeverityFieldsEnum.NAME)
              )
            ),
            customPrefix: !severityAsPriorityActive && (
              <div
                className={"severity-filter-option"}
                style={{
                  backgroundColor: getSeverityField?.(
                    option?.value?.[0],
                    SeverityFieldsEnum.COLOR
                  ),
                }}
              />
            ),
          }))
      );
    case "source":
      const { getDataSourceIcon } = getOptionsCallback(field ?? "") ?? {};
      return options?.map((option) => ({
        ...option,
        label: option?.value?.[0],
        customPrefix: (
          <div className={"data-source-filter-option"}>
            <img
              src={
                getDataSourceIcon?.(option?.value?.[0] as string, "S") as
                  | string
                  | undefined
              }
            />
          </div>
        ),
      }));
    case "analyst_instance_id": {
      const { getAnalystDisplayName } = getOptionsCallback(field ?? "") ?? {};
      return options?.map((option) => ({
        ...option,
        label: getAnalystDisplayName?.(option?.value?.[0]) ?? option?.label,
      }));
    }
    case "mitigation_level":
      return options?.map((option) => {
        const value = option?.value?.[0] as string | undefined;
        if (isEdrValueWithoutIcon(value)) {
          return {
            ...option,
            label: value ? resolveEdrDisplayLabel(value) : value,
          };
        }
        // Past the guard above, `value` is a truthy non-"N/A" BE level string,
        // so getEdrLevelMeta always resolves to the "data" variant at runtime.
        const { iconClass, colorClass } = getEdrLevelMeta(
          value as string
        ) as Extract<EdrLevelMeta, { variant: "data" }>;
        return {
          ...option,
          label: resolveEdrDisplayLabel(value as string),
          customPrefix: (
            <div className={"edr-mitigation-filter-option"}>
              <i className={`${iconClass} ${colorClass}`} />
            </div>
          ),
        };
      });

    default:
      return options;
  }
};

// ============================================================================
// EDR mitigation-level icon helpers (used by enrichFilterOptions above)
// ============================================================================

const EDR_NON_ELIGIBLE_VALUE = "N/A";

const isEdrValueWithoutIcon = (value?: string | null): boolean =>
  !value || value.toLowerCase() === EDR_NON_ELIGIBLE_VALUE.toLowerCase();

// ============================================================================
// Actual-status tree helpers
// ============================================================================

// A persisted actual_status filter value: an array of category/key descriptors.
interface ActualStatusFilterItem {
  type?: string;
  key?: string[];
}

export const getActualStatusNodes = (
  // The persisted filter value is polymorphic at the call sites (string, array,
  // date object); actual_status is always the array shape. Read it through the
  // array view so the `?.filter` chain mirrors main's untyped access exactly.
  fieldValue: unknown,
  treeData: TreeNode[] | undefined
): TreeNode[] => {
  const items = fieldValue as ActualStatusFilterItem[] | undefined;
  const categories = items
    ?.filter((item) => item?.type === "category")
    ?.reduce<unknown[]>((keysArr, category) => {
      return [...keysArr, category?.key];
    }, []);
  const keys = items
    ?.filter((item) => item?.type === "key")
    ?.reduce<number[]>((keysArr, sub_status) => {
      return [
        ...keysArr,
        ...(sub_status?.key?.map((item) => parseInt(item)) as number[]),
      ];
    }, []);
  const nodes: TreeNode[] = [];
  treeData?.[0]?.children?.forEach((category) => {
    if (categories?.includes(category.key)) {
      nodes.push(category);
    } else {
      category?.children?.forEach((sub_status) => {
        if (!!intersection(sub_status?.key as unknown[], keys)?.length) {
          nodes.push(sub_status);
        }
      });
    }
  });

  return nodes;
};

export const getActualStatusIcon = (
  type?: string,
  id?: string
): JSX.Element | null => {
  if (type === "category") {
    return <FindingStatusLogo category={id} />;
  } else {
    return null;
  }
};

// getTreeIconFnByFilterField returns getActualStatusIcon (declared above).
export const getTreeIconFnByFilterField = (
  field: string
): typeof getActualStatusIcon | null =>
  isActualStatusKey(field) ? getActualStatusIcon : null;
